import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import showToast from '../../components/Toast';
import { FiBriefcase, FiArrowRight, FiShield, FiCheck, FiArrowLeft } from 'react-icons/fi';
import AxiosInstance from '../../AxiosInstance';
import config from '../../config/frontend.config';
import './PasswordSetup.css'; // Reuse the same styles

const API_BASE_URL = config.api.baseURL + '/' + config.api.apiPath;

const companyNameValidationSchema = Yup.object({
  companyName: Yup.string()
    .required('Company name is required')
    .min(2, 'Company name must be at least 2 characters')
    .max(100, 'Company name must be less than 100 characters')
    .trim(),
});

function CompanySetup() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [companyNameSet, setCompanyNameSet] = useState(false);

  useEffect(() => {
    // Check if user is already logged in
    const token = config.getAuthToken();
    const userId = config.getUserId();
    
    if (!token || !userId) {
      navigate('/auth');
      return;
    }

    // Get user info
    const userData = config.getUserData();
    if (userData && userData.CompanyName && userData.CompanyName.trim() !== '') {
      // User already has company name set, check if password is needed
      const isNewGoogleUser = userData.isGoogleUser && (!userData.Password || userData.Password === '');
      if (isNewGoogleUser) {
        navigate('/auth/password-setup');
      } else {
        navigate('/');
      }
      return;
    }

    setUserInfo(userData);
  }, [navigate]);

  const handleCompanyNameSetup = async (values, { setSubmitting }) => {
    try {
      setLoading(true);
      const userId = config.getUserId();
      
      const response = await AxiosInstance.put(
        `${API_BASE_URL}/user/updateuser?UserId=${userId}`,
        {
          CompanyName: values.companyName.trim()
        }
      );

      if (response.data.statusCode === 200) {
        // Update local user data
        const updatedUserData = { ...userInfo, CompanyName: values.companyName.trim() };
        config.setUserData(updatedUserData);
        
        setCompanyNameSet(true);
        showToast.success('Company name set successfully!');
        
        // Check if password is also needed
        const isNewGoogleUser = userInfo.isGoogleUser && (!userInfo.Password || userInfo.Password === '');
        
        setTimeout(() => {
          if (isNewGoogleUser) {
            navigate('/auth/password-setup');
          } else {
            navigate('/');
          }
        }, 1000);
      } else {
        showToast.error(response.data.message || 'Failed to set company name');
        setSubmitting(false);
      }
    } catch (error) {
      console.error('Error setting company name:', error);
      showToast.error(error.response?.data?.message || 'Failed to set company name. Please try again.');
      setSubmitting(false);
    } finally {
      setLoading(false);
    }
  };

  const handleBackClick = () => {
    if (!companyNameSet) {
      setShowExitDialog(true);
    } else {
      navigate('/');
    }
  };

  const handleExitConfirm = () => {
    config.clearAuth();
    navigate('/auth');
  };

  const handleExitCancel = () => {
    setShowExitDialog(false);
  };

  if (!userInfo) {
    return (
      <div className="password-setup-container">
        <div className="password-setup-form-container">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="password-setup-container">
      {/* Background Section */}
      <div className="password-setup-background">
        <div className="password-setup-background-overlay"></div>
        <div className="password-setup-background-content">
          <div className="welcome-icon">
            <FiShield />
          </div>
          <h1>Welcome!</h1>
          <p>Complete your account setup by providing your company name</p>
          
          <div className="password-setup-features">
            <div className="feature">
              <div className="feature-icon">
                <FiCheck />
              </div>
              <span>Set up your company profile</span>
            </div>
            <div className="feature">
              <div className="feature-icon">
                <FiCheck />
              </div>
              <span>Access all features after setup</span>
            </div>
            <div className="feature">
              <div className="feature-icon">
                <FiCheck />
              </div>
              <span>Quick and easy setup process</span>
            </div>
          </div>
        </div>
      </div>

      {/* Form Section */}
      <div className="password-setup-form-container">
        <div className="password-setup-form">
          {/* Back Button */}
          <button 
            type="button" 
            className="password-setup-back-button"
            onClick={handleBackClick}
          >
            <FiArrowLeft />
            Back
          </button>

          <div className="password-setup-header">
            <h2>Set Your Company Name</h2>
            <p>Hello {userInfo.FirstName}! Please provide your company name to complete your account setup.</p>
          </div>

          <Formik
            initialValues={{
              companyName: ''
            }}
            validationSchema={companyNameValidationSchema}
            onSubmit={handleCompanyNameSetup}
          >
            {({ values, errors, touched, isSubmitting }) => (
              <Form className="password-setup-form-fields">
                <div className="form-group">
                  <label htmlFor="companyName">COMPANY NAME</label>
                  <div className="input-group">
                    <div className="input-icon">
                      <FiBriefcase />
                    </div>
                    <Field
                      type="text"
                      id="companyName"
                      name="companyName"
                      placeholder="Enter your company name"
                      className={`form-input ${errors.companyName && touched.companyName ? 'error' : ''}`}
                      disabled={loading || companyNameSet}
                    />
                  </div>
                  <ErrorMessage name="companyName" component="div" className="error-message" />
                </div>

                <button
                  type="submit"
                  className="password-setup-button"
                  disabled={loading || companyNameSet || isSubmitting}
                >
                  {loading ? (
                    <div className="button-loading">
                      <div className="button-spinner"></div>
                      Setting up...
                    </div>
                  ) : companyNameSet ? (
                    <>
                      <FiCheck />
                      Company Name Set!
                    </>
                  ) : (
                    <>
                      Complete Setup
                      <FiArrowRight />
                    </>
                  )}
                </button>
              </Form>
            )}
          </Formik>
        </div>
      </div>

      {/* Exit Dialog */}
      {showExitDialog && (
        <div className="exit-dialog-overlay" onClick={handleExitCancel}>
          <div className="exit-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="exit-dialog-icon">
              <FiShield />
            </div>
            <h3>Exit Setup?</h3>
            <p>
              You haven't completed your company setup yet. Setting a company name is <strong>important</strong> because:
            </p>
            <ul className="exit-dialog-reasons">
              <li>It helps identify your organization</li>
              <li>It's required for multi-tenant features</li>
              <li>It provides better account management</li>
            </ul>
            <p className="exit-dialog-warning">
              <strong>You can set it later from Settings, but it's recommended to set it now.</strong>
            </p>
            <div className="exit-dialog-buttons">
              <button 
                className="exit-dialog-continue"
                onClick={handleExitCancel}
              >
                Continue Setup
              </button>
              <button 
                className="exit-dialog-leave"
                onClick={handleExitConfirm}
              >
                Exit Setup
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CompanySetup;

